<template>
  <section class="page" data-testid="page-audit">
    <header class="page-header">
      <div>
        <h1>安全审计</h1>
        <p>登录、鉴权失败、高危管理操作和敏感工作流记录。</p>
      </div>
      <div class="actions">
        <button class="button" :disabled="!filteredEvents.length" @click="copySnapshot">复制当前视图</button>
        <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>

    <div class="metrics-grid">
      <MetricCard label="审计事件" :value="events.length" />
      <MetricCard label="失败/拦截" :value="blockedCount" />
      <MetricCard label="高危变更" :value="mutationCount" />
      <MetricCard label="唯一客户端" :value="uniqueClientCount" />
      <MetricCard label="当前匹配" :value="filteredEvents.length" />
      <MetricCard label="最近事件" :value="events[0] ? formatTime(events[0].ts) : '-'" />
      <MetricCard label="安全状态" :value="security?.status || '-'" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="panel">
      <div class="toolbar">
        <input v-model="query" placeholder="搜索事件、路径、操作者、IP、详情" />
        <select v-model="filter.eventType" :disabled="loading" @change="load">
          <option value="">全部事件</option>
          <option v-for="item in eventTypes" :key="item" :value="item">{{ eventLabel(item) }}</option>
        </select>
        <select v-model.number="filter.limit" :disabled="loading" @change="load">
          <option :value="50">最近 50 条</option>
          <option :value="100">最近 100 条</option>
          <option :value="200">最近 200 条</option>
        </select>
        <button class="button" :class="{ primary: riskFilter === 'all' }" @click="riskFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: riskFilter === 'blocked' }" @click="riskFilter = 'blocked'">失败/拦截</button>
        <button class="button" :class="{ primary: riskFilter === 'mutation' }" @click="riskFilter = 'mutation'">高危变更</button>
        <button class="button" :class="{ primary: riskFilter === 'auth' }" @click="riskFilter = 'auth'">登录鉴权</button>
        <button class="button" :class="{ primary: riskFilter === 'sensitive' }" @click="riskFilter = 'sensitive'">敏感读取</button>
        <button class="button" :disabled="loading || !filter.eventType" @click="clearFilter">清除筛选</button>
      </div>

      <DataTable :columns="columns" :rows="filteredEvents" row-key="id">
        <template #ts="{ row }">{{ formatTime(row.ts) }}</template>
        <template #event_type="{ row }">
          <span class="status-badge" :class="eventClass(row.event_type)">{{ eventLabel(row.event_type) }}</span>
        </template>
        <template #status_code="{ row }">
          <span class="status-badge" :class="statusClass(row.status_code)">HTTP {{ row.status_code || "-" }}</span>
        </template>
        <template #actor="{ row }">{{ row.actor || "-" }}</template>
        <template #request="{ row }">
          <div class="request-cell">
            <strong>{{ row.method || "-" }}</strong>
            <span>{{ row.path || "-" }}</span>
          </div>
        </template>
        <template #client="{ row }">
          <div class="client-cell">
            <strong>{{ row.ip_address || "-" }}</strong>
            <span>{{ compactAgent(row.user_agent) }}</span>
          </div>
        </template>
        <template #details="{ row }">
          <pre>{{ formatDetails(row.details) }}</pre>
        </template>
        <template #action="{ row }">
          <div class="row-actions">
            <button class="button small" @click="inspectEvent(row)">查看</button>
            <button class="button small" @click="copyEvent(row)">复制</button>
          </div>
        </template>
      </DataTable>
    </div>

    <div class="split">
      <div class="panel">
        <h2>风险速览</h2>
        <div class="risk-list">
          <div v-for="item in riskNotes" :key="item.label" class="risk-row" :class="item.level">
            <strong>{{ item.label }}</strong>
            <span>{{ item.detail }}</span>
          </div>
        </div>
        <h3>客户端聚合</h3>
        <DataTable :columns="clientColumns" :rows="topClients" row-key="ip">
          <template #lastSeen="{ row }">{{ formatTime(row.lastSeen) }}</template>
          <template #blocked="{ row }">{{ row.blocked }}/{{ row.total }}</template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>事件详情</h2>
        <div v-if="selectedEvent" class="event-detail">
          <div class="detail-head">
            <span class="status-badge" :class="eventClass(selectedEvent.event_type)">{{ eventLabel(selectedEvent.event_type) }}</span>
            <button class="button small" @click="copyEvent(selectedEvent)">复制 JSON</button>
          </div>
          <div class="detail-grid">
            <div><span>时间</span><strong>{{ formatTime(selectedEvent.ts) }}</strong></div>
            <div><span>操作者</span><strong>{{ selectedEvent.actor || "-" }}</strong></div>
            <div><span>客户端</span><strong>{{ selectedEvent.ip_address || "-" }}</strong></div>
            <div><span>请求</span><strong>{{ selectedEvent.method || "-" }} {{ selectedEvent.path || "-" }}</strong></div>
          </div>
          <pre>{{ JSON.stringify(selectedEvent, null, 2) }}</pre>
        </div>
        <div v-else class="empty">选择一条事件查看完整证据。</div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import MetricCard from "../ui/MetricCard.vue";

type AuditEvent = {
  id: number;
  ts: string;
  actor: string;
  event_type: string;
  method: string;
  path: string;
  status_code: number;
  ip_address: string;
  user_agent: string;
  details: Record<string, unknown>;
};

type AuditPayload = {
  events: AuditEvent[];
  security: { status?: string; message?: string };
};

const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyJson: copyEventJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择详情");
const { copyJson: copySnapshotJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择事件详情");
const query = ref("");
const riskFilter = ref<"all" | "blocked" | "mutation" | "auth" | "sensitive">("all");
const events = ref<AuditEvent[]>([]);
const security = ref<AuditPayload["security"] | null>(null);
const selectedEvent = ref<AuditEvent | null>(null);
const filter = reactive({ eventType: "", limit: 100 });
const toast = useToastStore();

const columns = [
  { key: "ts", label: "时间" },
  { key: "event_type", label: "事件" },
  { key: "status_code", label: "状态" },
  { key: "actor", label: "操作者" },
  { key: "request", label: "请求" },
  { key: "client", label: "客户端" },
  { key: "details", label: "详情" },
  { key: "action", label: "操作" }
];

const clientColumns = [
  { key: "ip", label: "IP" },
  { key: "blocked", label: "失败/总数" },
  { key: "lastPath", label: "最近路径" },
  { key: "lastSeen", label: "最近时间" }
];

const blockedEventTypes = ["login_failed", "login_rate_limited", "csrf_rejected", "rate_limited", "locked_rejected", "bearer_login_failed"];
const mutationEventTypes = ["config_update", "persona_publish", "persona_rollback", "memory_admin", "plugin_reload", "plugin_action", "learning_admin", "openclaw_admin", "napcat_admin", "onebot_admin", "upload", "build_persona", "admin_mutation"];
const authEventTypes = ["login_failed", "login_success", "logout", "login_rate_limited", "csrf_rejected", "rate_limited", "locked_rejected", "bearer_login_failed"];
const sensitiveReadEventTypes = ["plugin_data_view"];

const eventTypes = computed(() => {
  const known = [
    "login_failed",
    "login_rate_limited",
    "login_success",
    "logout",
    "csrf_rejected",
    "rate_limited",
    "config_update",
    "persona_publish",
    "persona_rollback",
    "memory_admin",
    "plugin_reload",
    "plugin_action",
    "plugin_data_view",
    "learning_admin",
    "openclaw_admin",
    "napcat_admin",
    "onebot_admin",
    "upload",
    "admin_mutation"
  ];
  return Array.from(new Set([...known, ...events.value.map((item) => item.event_type).filter(Boolean)]));
});
const blockedCount = computed(() => events.value.filter((item) => item.status_code >= 400).length);
const mutationCount = computed(() => events.value.filter((item) => mutationEventTypes.includes(item.event_type)).length);
const uniqueClientCount = computed(() => new Set(events.value.map((item) => item.ip_address).filter(Boolean)).size);
const filteredEvents = computed(() => {
  const text = query.value.trim().toLowerCase();
  return events.value.filter((item) => {
    if (riskFilter.value === "blocked" && !isBlocked(item)) return false;
    if (riskFilter.value === "mutation" && !mutationEventTypes.includes(item.event_type)) return false;
    if (riskFilter.value === "auth" && !authEventTypes.includes(item.event_type)) return false;
    if (riskFilter.value === "sensitive" && !sensitiveReadEventTypes.includes(item.event_type)) return false;
    if (!text) return true;
    return auditSearchText(item).includes(text);
  });
});
const topClients = computed(() => {
  const groups = new Map<string, { ip: string; total: number; blocked: number; lastSeen: string; lastPath: string }>();
  for (const item of filteredEvents.value) {
    const ip = item.ip_address || "-";
    const current = groups.get(ip) || { ip, total: 0, blocked: 0, lastSeen: item.ts, lastPath: item.path || "-" };
    current.total += 1;
    if (isBlocked(item)) current.blocked += 1;
    if (new Date(item.ts).getTime() >= new Date(current.lastSeen).getTime()) {
      current.lastSeen = item.ts;
      current.lastPath = item.path || "-";
    }
    groups.set(ip, current);
  }
  return Array.from(groups.values())
    .sort((a, b) => b.blocked - a.blocked || b.total - a.total)
    .slice(0, 8);
});
const riskNotes = computed(() => {
  const blocked = filteredEvents.value.filter(isBlocked);
  const mutations = filteredEvents.value.filter((item) => mutationEventTypes.includes(item.event_type));
  const sensitiveReads = filteredEvents.value.filter((item) => sensitiveReadEventTypes.includes(item.event_type));
  const noisyClient = topClients.value[0];
  return [
    {
      label: "失败/拦截",
      detail: blocked.length ? `${blocked.length} 条需要排查，最近 ${formatTime(blocked[0].ts)}` : "当前视图没有失败或拦截事件",
      level: blocked.length ? "error" : "ok"
    },
    {
      label: "高危变更",
      detail: mutations.length ? `${mutations.length} 条配置、人设、插件或学习管理变更` : "当前视图没有高危变更",
      level: mutations.length ? "warning" : "ok"
    },
    {
      label: "敏感读取",
      detail: sensitiveReads.length ? `${sensitiveReads.length} 条插件数据等敏感读取` : "当前视图没有敏感读取",
      level: sensitiveReads.length ? "warning" : "ok"
    },
    {
      label: "活跃客户端",
      detail: noisyClient ? `${noisyClient.ip}：${noisyClient.blocked}/${noisyClient.total} 失败/总数` : "暂无客户端记录",
      level: noisyClient?.blocked ? "warning" : "ok"
    }
  ];
});

function eventLabel(type: string) {
  const labels: Record<string, string> = {
    login_failed: "登录失败",
    login_rate_limited: "登录限流",
    login_success: "登录成功",
    logout: "退出登录",
    csrf_rejected: "CSRF 拦截",
    rate_limited: "频率限制",
    locked_rejected: "锁定拦截",
    bearer_login_failed: "Token 失败",
    config_update: "配置更新",
    persona_publish: "人设发布",
    persona_rollback: "人设回滚",
    memory_admin: "记忆管理",
    plugin_reload: "插件重载",
    plugin_action: "插件操作",
    plugin_data_view: "插件数据",
    learning_admin: "学习管理",
    openclaw_admin: "project-internal",
    napcat_admin: "NapCat 管理",
    onebot_admin: "OneBot 管理",
    upload: "上传",
    build_persona: "构建人设",
    admin_mutation: "管理变更"
  };
  return labels[type] || type || "-";
}

function eventClass(type: string) {
  if (blockedEventTypes.includes(type)) return "error";
  if (mutationEventTypes.includes(type) || sensitiveReadEventTypes.includes(type)) return "degraded";
  return "ok";
}

function statusClass(status: number) {
  if (status >= 500) return "error";
  if (status >= 400) return "degraded";
  return "ok";
}

function formatTime(value: string) {
  return value ? new Date(value).toLocaleString("zh-CN") : "-";
}

function compactAgent(value: string) {
  const text = String(value || "");
  return text.length > 56 ? `${text.slice(0, 56)}...` : text || "-";
}

function formatDetails(value: Record<string, unknown>) {
  return Object.keys(value || {}).length ? JSON.stringify(value, null, 2) : "-";
}

function isBlocked(item: AuditEvent) {
  return item.status_code >= 400 || blockedEventTypes.includes(item.event_type);
}

function auditSearchText(item: AuditEvent) {
  return [
    item.id,
    item.ts,
    item.actor,
    item.event_type,
    eventLabel(item.event_type),
    item.method,
    item.path,
    item.status_code,
    item.ip_address,
    item.user_agent,
    formatDetails(item.details)
  ].join(" ").toLowerCase();
}

function clearFilter() {
  filter.eventType = "";
  load();
}

function inspectEvent(item: AuditEvent) {
  selectedEvent.value = item;
}

async function copyEvent(item: AuditEvent) {
  await copyEventJson(item, `已复制审计事件 #${item.id}`);
}

async function copySnapshot() {
  const payload = {
    filter: { event_type: filter.eventType || "all", limit: filter.limit, risk: riskFilter.value, query: query.value.trim() },
    security: security.value || {},
    counts: {
      loaded: events.value.length,
      matched: filteredEvents.value.length,
      blocked: filteredEvents.value.filter(isBlocked).length,
      mutations: filteredEvents.value.filter((item) => mutationEventTypes.includes(item.event_type)).length
    },
    events: filteredEvents.value.slice(0, 30)
  };
  await copySnapshotJson(payload, `已复制 ${payload.events.length} 条审计证据`);
}

async function load() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const params = new URLSearchParams({ limit: String(filter.limit) });
    if (filter.eventType) params.set("event_type", filter.eventType);
    const payload = await api<AuditPayload>(`/api/security/audit?${params.toString()}`);
    events.value = payload.events || [];
    security.value = payload.security || {};
    selectedEvent.value = events.value[0] || null;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "审计事件加载失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.request-cell,
.client-cell {
  display: grid;
  gap: 4px;
  min-width: 140px;
}

.request-cell span,
.client-cell span {
  color: var(--muted);
  font-size: 12px;
  word-break: break-all;
}

td pre {
  max-height: 140px;
  min-width: 180px;
  font-size: 12px;
}

.toolbar input {
  min-width: min(360px, 100%);
  flex: 1 1 280px;
}

.row-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.risk-list {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.risk-row {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
}

.risk-row strong,
.risk-row span {
  display: block;
}

.risk-row span {
  color: var(--muted);
  margin-top: 4px;
}

.risk-row.error {
  border-color: #743434;
}

.risk-row.warning {
  border-color: #78612d;
}

.risk-row.ok {
  border-color: #386f47;
}

.event-detail {
  display: grid;
  gap: 12px;
}

.detail-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}

.detail-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}

.detail-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.detail-grid strong {
  display: block;
  margin-top: 5px;
  word-break: break-all;
}
</style>
